import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, takeUntil } from 'rxjs';
import { IdentityReportsService } from '@app/core/services/identity-reports.service';
import { Client } from '@app/core/types/client';
import { BrandCodes } from '@app/core/types/brand';

@Component({
  selector: 'itfg-identity-verification',
  templateUrl: './identity-verification.component.html',
  styleUrl: './identity-verification.component.scss',
  standalone: false,
})
export class IdentityVerificationComponent implements OnChanges, OnDestroy {
  @Input() client: Client;

  verifications: any[] = [];
  currentIndex = 0;
  currentDetail: any | null = null;
  loadingList = false;
  loadingDetail = false;

  private _unsubscribe = new Subject<void>();

  // ── Display config ────────────────────────────────────────────────

  readonly STATUS_ICON: Record<string, string> = {
    APPROVED: 'check_circle',
    COMPLETED: 'check_circle',
    REJECTED: 'cancel',
    PENDING: 'hourglass_empty',
    EXPIRED: 'schedule',
  };

  readonly STATUS_CLASS: Record<string, string> = {
    APPROVED: 'approved',
    COMPLETED: 'approved',
    REJECTED: 'rejected',
    PENDING: 'pending',
    EXPIRED: 'expired',
  };

  readonly PROC_COLOR: Record<string, string> = {
    SHUFTI: '#6a1b9a',
    MINDEE: '#1565c0',
    IPROOV: '#00695c',
    OPERATOR: '#e65100',
    INTERNAL: '#546e7a',
    EXTERNAL: '#4527a0',
    SYSTEM: '#90a4ae',
  };

  readonly DOC_ROWS = [
    { label: 'ID Front', statusKey: 'idFrontStatus', reasonKey: 'idFrontReason', docKey: 'idCardFront' },
    { label: 'ID Back', statusKey: 'idBackStatus', reasonKey: 'idBackReason', docKey: 'idCardBack' },
    { label: 'Selfie', statusKey: 'selfieStatus', reasonKey: 'selfieReason', docKey: 'selfie' },
  ];

  // ── Stats ─────────────────────────────────────────────────────────

  get total() { return this.verifications.length; }
  get completed() { return this.verifications.filter(v => v.status === 'COMPLETED').length; }
  get rejected() { return this.verifications.filter(v => v.status === 'REJECTED').length; }
  get pending() { return this.verifications.filter(v => v.status === 'PENDING').length; }
  get last24h() {
    const cutoff = Date.now() - 86_400_000;
    return this.verifications.filter(v => new Date(v.startedAt).getTime() > cutoff).length;
  }

  // ── Derived ───────────────────────────────────────────────────────

  get eventsDesc(): any[] {
    return [...(this.currentDetail?.events ?? [])].reverse();
  }

  /** Worst-case doc status bubbles up to the consolidated badge. */
  get consolidatedDocStatus(): string {
    const statuses = this.DOC_ROWS.map(r => this.currentDetail?.[r.statusKey]);
    if (statuses.includes('REJECTED')) return 'REJECTED';
    if (statuses.includes('PENDING')) return 'PENDING';
    return 'APPROVED';
  }

  constructor(
    private service: IdentityReportsService,
    private snackBar: MatSnackBar,
  ) { }

  // ── Lifecycle ─────────────────────────────────────────────────────

  ngOnChanges(): void {
    if (this.client?.id && this.client?.civilId) {
      this.loadList();
    }
  }

  ngOnDestroy(): void {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }

  // ── Data ──────────────────────────────────────────────────────────

  loadList(): void {
    this.loadingList = true;
    this.verifications = [];
    this.currentDetail = null;
    this.currentIndex = 0;

    this.service
      .getIdentityVerificationList(this.client.civilId, this.client.brand.code as BrandCodes)
      .pipe(takeUntil(this._unsubscribe))
      .subscribe({
        next: (data: any[]) => {
          this.verifications = data;
          if (data.length) this.loadDetail(data[0].id);
        },
        error: e => console.error(e),
        complete: () => { this.loadingList = false; },
      });
  }

  loadDetail(id: number): void {
    this.loadingDetail = true;
    this.currentDetail = null;

    this.service
      .getIdentityVerificationReport(id)
      .pipe(takeUntil(this._unsubscribe))
      .subscribe({
        next: (data: any) => { this.currentDetail = data; },
        error: e => console.error(e),
        complete: () => { this.loadingDetail = false; },
      });
  }

  navigate(direction: -1 | 1): void {
    const next = this.currentIndex + direction;
    if (next < 0 || next >= this.verifications.length) return;
    this.currentIndex = next;
    this.loadDetail(this.verifications[next].id);
  }

  // ── Actions ───────────────────────────────────────────────────────

  startVerification(): void {
    this.service.startIdentityVerification(this.client.civilId, this.client.brand.code as BrandCodes)
      .pipe(takeUntil(this._unsubscribe))
      .subscribe({
        next: (data: any) => { this.loadList(); },
        error: e => console.error(e),
        complete: () => { },
      });
    // TODO: call service
    this.snackBar.open('Verification started', 'OK', { duration: 2000 });
  }

  decide(action: 'approve' | 'reject' | 'escalate'): void {
    // TODO: call service
    const label = { approve: 'Approved', reject: 'Rejected', escalate: 'Escalated' }[action];
    this.snackBar.open(`${label} #${this.currentDetail?.id}`, 'OK', { duration: 2000 });
  }

  // ── Template helpers ──────────────────────────────────────────────

  statusClass(status: string): string {
    return this.STATUS_CLASS[status] ?? 'expired';
  }

  procColor(processor: string): string {
    return this.PROC_COLOR[processor] ?? '#546e7a';
  }

  isFailEvent(type: string): boolean { return /FAIL|REJECT|ERROR/.test(type); }
  isSuccessEvent(type: string): boolean { return /APPROVE|COMPLETE|MATCH/.test(type); }

  duration(a: string, b: string | null): string {
    if (!b) return 'ongoing';
    const s = Math.round((+new Date(b) - +new Date(a)) / 1000);
    return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
  }

  relTime(startTs: string, eventTs: string): string {
    const s = Math.round((+new Date(eventTs) - +new Date(startTs)) / 1000);
    if (s <= 0) return '';
    if (s < 60) return `+${s}s`;
    return `+${Math.floor(s / 60)}m`;
  }
}